import { createContext, useContext } from "react";
import { useStreamingChat } from "@/hooks/use-streaming-chat";
import { useAuth } from "@/hooks/use-auth";
import type { Message } from "@shared/types";

interface ChatContextValue {
  messages: Message[];
  isStreaming: boolean;
  currentResponse: string;
  error: string | null;
  conversationId: string | null;
  sendMessage: (content: string) => Promise<void>;
  stopStreaming: () => void;
  clearChat: () => void;
  loadConversation: (id: string) => Promise<void>;
  submitFeedback: (messageId: string, feedback: "up" | "down" | null) => Promise<void>;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function useChatContext(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChatContext must be used within ChatProvider");
  return ctx;
}

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const userId = user?.id ?? "anonymous";
  const chat = useStreamingChat(userId);

  if (loading) return null;

  return <ChatContext.Provider value={chat}>{children}</ChatContext.Provider>;
}
